import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, LoadingSpinner } from 'yankees-react-utils';
import { useOktaQuery, Stats } from '../../../../data';
import useConfig from '../../../../utils/useConfig';
import usePlayerSelector from '../../../../utils/usePlayerSelector';
import PlayerSelector from '../../../../components/PlayerSelector';

const Filter = ({
    setIsLoadingFilters,
    pitcher,
    setPitcher,
    year,
    setYear,
    level,
    setLevel,
    IsLhb,
    setIsLhb,
    role,
    setRole,
    setHasSearched,
}) => {
    const { t: translate } = useTranslation();
    const { CurrentYear, UsePreviousYearData } = useConfig();
    const player = usePlayerSelector();

    const [innerPitcher, setInnerPitcher] = useState(pitcher);
    const [innerYear, setInnerYear] = useState(year);
    const [innerLevel, setInnerLevel] = useState(level);
    const [innerIsLhb, setInnerIsLhb] = useState(IsLhb);
    const [innerRole, setInnerRole] = useState(role);

    const allYearOptions = [
        { name: `${CurrentYear - 1}-${CurrentYear.toString().substr(2)}`, value: { startYear: CurrentYear - 1, endYear: CurrentYear }, key: -1 },
        { name: `${CurrentYear - 2}-${CurrentYear.toString().substr(2)}`, value: { startYear: CurrentYear - 2, endYear: CurrentYear }, key: -2 },
        ...([CurrentYear, CurrentYear - 1, CurrentYear - 2].map((y) => ({ name: y, value: { startYear: y, endYear: y }, key: y }))),
    ];

    const yearOptions = window.$IsDugoutApp ? [UsePreviousYearData ? allYearOptions[0] : allYearOptions[2]] : allYearOptions;

    const isLhbOptions = [
        { name: translate('common.all'), value: null },
        { name: translate('baseball.rhh'), value: 0 },
        { name: translate('baseball.lhh'), value: 1 },
    ];

    const roleOptions = [
        { name: translate('common.all'), value: null },
        { name: translate('baseball.positions.abbr-pitcher-starting'), value: 1 },
        { name: translate('baseball.positions.abbr-pitcher-relief'), value: 0 },
    ];

    const [levelOptions, setLevelOptions] = useState([]);

    const { data: pitchingSeasons, isFetching: isLoadingPitchingSeasons } = useOktaQuery(
        ['pitchingSeasons', innerPitcher?.PlayerID, player.PlayerID],
        (t) => Stats.seasonPitching(t, { playerid: innerPitcher?.PlayerID }),
        {
            onSuccess: () => setIsLoadingFilters(false),
            enabled: !!innerPitcher,
            staleTime: Infinity,
        },
    );

    useEffect(() => {
        setInnerLevel(null);
        if (player.IsCatcher) setHasSearched(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [innerPitcher]);

    useEffect(() => {
        if (pitchingSeasons && pitchingSeasons.length) {
            // set initial level to use and build list of selectable levels
            // just levels in the year range defined above
            const levels = pitchingSeasons
                .filter((ps) => ps.YearID >= CurrentYear - 2 && [8, 1, 2, 3, 4, 5, 10].some((x) => x === ps.Level.LevelID))
                .map((ps) => ps.Level);
            // throw out dupes and sort by level sortorder
            let useLevels = [...new Set(levels.map((lv) => lv.LevelID))]
                .map((levelId) => levels.find((lv) => lv.LevelID === levelId))
                .sort((a, b) => a.SortOrder - b.SortOrder)
                .map((lv) => ({
                    name: lv.Abbr,
                    value: lv.LevelID,
                }));
            // add MLB+MIN and MIN if appropriate
            const hasMIN = useLevels.some((lv) => [1, 2, 3, 4, 5, 10].some((x) => x === lv.value));
            const hasMLB = useLevels.some((lv) => lv.value === 8);
            if (hasMIN) {
                if (hasMLB) useLevels.push({ name: translate('sections.reports.pitcher.level-min-mlb'), value: null });
                useLevels.push({ name: translate('sections.reports.pitcher.level-min'), value: 7 });
            }
            if (window.$IsDugoutApp) {
                useLevels = useLevels.filter((lv) => [7, 8].includes(lv.value));
            }
            setLevelOptions(useLevels);
            const atLevel = useLevels.find((lv) => lv.value === player.Team.LevelID);
            setInnerLevel(atLevel ?? useLevels[0]);
            setLevel(atLevel ?? useLevels[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pitchingSeasons]);

    useEffect(() => {
        setYear(yearOptions[UsePreviousYearData || window.$IsDugoutApp ? 0 : 2]);
        setInnerYear(yearOptions[UsePreviousYearData || window.$IsDugoutApp ? 0 : 2]);
        setIsLhb(isLhbOptions[0]);
        setInnerIsLhb(isLhbOptions[0]);
        setRole(roleOptions[0]);
        setInnerRole(roleOptions[0]);
        if (pitchingSeasons) setIsLoadingFilters(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applyFilter = () => {
        setPitcher(innerPitcher);
        setYear(innerYear);
        setLevel(innerLevel);
        setIsLhb(innerIsLhb);
        setRole(innerRole);
        setHasSearched(true);
    };

    return (
        <div>
            <div className="d-flex justify-content-end align-items-center">
                {player.IsCatcher && (
                    <>
                        <div className="me-1">{translate('baseball.pitcher_one')}:</div>
                        <PlayerSelector
                            teamid={player.Team.TeamID}
                            position="pitcher"
                            player={innerPitcher}
                            onSelect={setInnerPitcher}
                            title={translate('common.select-a-pitcher')}
                        />
                    </>
                )}
                <div className="ms-3 me-1">{translate('common.year')}:</div>
                <Dropdown
                    align="end"
                    options={yearOptions}
                    value={innerYear?.name ?? CurrentYear}
                    onSelect={setInnerYear}
                />
                {isLoadingPitchingSeasons ? <LoadingSpinner />
                    : (
                        <>
                            <div className="ms-3 me-1">{translate('baseball.level_one')}:</div>
                            <Dropdown
                                align="end"
                                options={levelOptions}
                                value={innerLevel?.name}
                                onSelect={setInnerLevel}
                                disabled={!innerPitcher}
                            />
                        </>
                    )}
                {!window.$IsDugoutApp && (
                    <>
                        <div className="ms-3 me-1">{translate('baseball.batter-hand')}:</div>
                        <Dropdown
                            align="end"
                            options={isLhbOptions}
                            value={innerIsLhb?.name}
                            onSelect={setInnerIsLhb}
                        />
                        <div className="ms-3 me-1">{translate('sections.reports.pitcher.role')}:</div>
                        <Dropdown
                            align="end"
                            options={roleOptions}
                            value={innerRole?.name}
                            onSelect={setInnerRole}
                        />
                    </>
                )}
                <Button variant="primary" className="ms-3" onClick={applyFilter} disabled={!innerPitcher || !innerLevel}>Apply</Button>
            </div>
        </div>
    );
};

export default Filter;
